/**
 * Channel-Categories Invariants
 *
 * Dedicated permission matrix + state-consistency normalization for the
 * test-channel category mapper. Extracted from the domain `invariants.ts` so
 * the category-mapping rules live next to the category state they govern; the
 * domain pipeline composes these via `invariants.ts`.
 *
 * Permission matrix ( per command ):
 * - `categories/map`, `categories/unmap`, `categories/search/set`
 *     → `user` ( the operator drives mapping / searching ) AND `url`
 *       ( deep-link hydration of a mapping or a search query ).
 * - `categories/set`, `categories/search/results/set`,
 *   `categories/loading/*`
 *     → `api` ( subscription snapshot, ML search results, loading flips ).
 * - `policy` source gets full access ( internal coordination ).
 * - `ai` is blocked from map / unmap ( mutating actions ); other category
 *   commands are not AI-relevant and stay blocked here.
 */

import { CommandMeta } from '@commandDrivenStateEngine/core/types';
import { ChannelsState, ChannelCategoryRow } from './state';
import { ChannelsCommand } from './commands';

const CATEGORY_PREFIX = 'integrations/channels/categories/';

/**
 * Narrow a command to the category namespace.
 */
export const isCategoryCommand = (
  command: ChannelsCommand
): boolean => command.type.startsWith( CATEGORY_PREFIX );

/**
 * Commands the operator ( and deep-link URL ) may issue: mapping, unmapping,
 * setting the search query, requesting a channel resync, and paging more search
 * results ( infinite scroll ). The latter two are user-driven UI triggers; both
 * only flip a `pending*` flag the policy drains, so allowing the `url` source is
 * harmless ( no deep-link issues them ).
 */
const USER_AND_URL_CATEGORY_COMMANDS = new Set<ChannelsCommand['type']>([
  'integrations/channels/categories/map',
  'integrations/channels/categories/unmap',
  'integrations/channels/categories/search/set',
  'integrations/channels/categories/resync',
  'integrations/channels/categories/search/loadMore',
  'integrations/channels/categories/list/setPage',
  'integrations/channels/categories/list/setSort',
  'integrations/channels/categories/list/setSearchQuery',
  'integrations/channels/categories/list/toggleMappingStatus',
  'integrations/channels/categories/list/clearFilters'
]);

/**
 * Commands carrying backend-sourced data ( subscription snapshot, ML search
 * results ) and loading flips. Only the `api` source may issue these. Includes
 * the resync + load-more bookkeeping the policy dispatches ( clear the drained
 * intent, append the next page, flip the resync / load-more spinners ).
 */
const API_CATEGORY_COMMANDS = new Set<ChannelsCommand['type']>([
  'integrations/channels/categories/set',
  'integrations/channels/categories/search/results/set',
  'integrations/channels/categories/search/results/append',
  'integrations/channels/categories/pending/clear',
  'integrations/channels/categories/resync/clear',
  'integrations/channels/categories/aiRun/set',
  'integrations/channels/categories/loading/setCategories',
  'integrations/channels/categories/loading/setSearch',
  'integrations/channels/categories/loading/setSearchLoadingMore',
  'integrations/channels/categories/loading/setResyncing'
]);

/**
 * Permission control for category commands. Assumes the caller has already
 * confirmed `isCategoryCommand( command )` is true.
 */
export const canApplyCategoryCommand = ({
  command,
  meta
}: {
  command: ChannelsCommand;
  meta: CommandMeta;
}): boolean => {
  const { type } = command;
  const { source } = meta;

  // Policy: full access for internal coordination.
  if ( source === 'policy' ) return true;

  // API: only data / results / loading commands.
  if ( source === 'api' ) return API_CATEGORY_COMMANDS.has( type );

  // User & URL: map / unmap / search query.
  if ( source === 'user' || source === 'url' ) {
    return USER_AND_URL_CATEGORY_COMMANDS.has( type );
  }

  // AI ( and any unknown source ): no category command.
  return false;
};

/**
 * Functional-map normalization for category rows.
 *
 * Invariant: a single `externalId` is mapped to AT MOST one Teselly target on
 * the client. The optimistic `categories/map` handler updates the matching row,
 * but a stale row ( e.g. a duplicate `externalId` reconciled from an out-of-order
 * subscription frame ) could leave two rows claiming a mapping for the same
 * external category. This collapses duplicates so the last write wins and never
 * leaves two targets for one `externalId`.
 *
 * The normalization is a no-op for the common ( unique-`externalId` ) case, so
 * it does not allocate a new array unless a duplicate is actually present.
 */
export const normalizeCategories = ( state: ChannelsState ): ChannelsState => {
  const { categories } = state.channelDetail;

  // Detect duplicate externalIds without allocating in the common case.
  const seen = new Set<string>();
  let hasDuplicate = false;
  for ( const category of categories ) {
    if ( seen.has( category.externalId )) {
      hasDuplicate = true;
      break;
    }
    seen.add( category.externalId );
  }

  if ( !hasDuplicate ) return state;

  // Collapse to one row per externalId, keeping the LAST occurrence ( the most
  // recent write ) so mapping one externalId never leaves two targets.
  const byExternalId = new Map<string, ChannelCategoryRow>();
  for ( const category of categories ) {
    byExternalId.set( category.externalId, category );
  }

  return {
    ...state,
    channelDetail: {
      ...state.channelDetail,
      categories: Array.from( byExternalId.values())
    }
  };
};
